import useAuth from '@/auth/useAuth';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useGetUser, useUpdateUser } from '@/service/user/hook';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import ChangePassword from './change-password';

const Profile: React.FC = () => {
  const { session } = useAuth();
  const { data } = useGetUser({
    enabled: false,
  });

  const isShowChangePassword =
    session?.user?.app_metadata?.provider === 'email';

  const { mutateAsync } = useUpdateUser();

  const formSchema = z.object({
    name: z.string().min(2).max(50),
    email: z.string().email(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: session?.user?.email,
      name: data?.name as string,
    },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    await mutateAsync({
      name: data.name,
      email: session?.user?.email,
    });
    toast.message('Profile updated');
  };

  return (
    <div className="container flex flex-col gap-3">
      <p className="text-xl font-semibold">Your profile</p>
      <div className="flex flex-col gap-8">
        <div>
          <Separator className="mb-6" />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-2">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email address</FormLabel>
                      <FormDescription>
                        The email address associated with this account
                      </FormDescription>
                      <FormControl>
                        <Input className="max-w-sm" {...field} disabled />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input className="max-w-sm" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  className="mt-4 self-start"
                  type="submit"
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </div>
        {isShowChangePassword && (
          <>
            <Separator />
            <ChangePassword />
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
